import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject, signal, Signal, computed } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { ApiResponse } from '../models/api-response';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { SnackbarService } from './snackbar.service';
import { API_ENDPOINTS, buildEndpoint } from '../api-endpoints-v2';
import { ReportSectionsV2Response, SectionV2 } from '../models/v2models/report-sections-v2';
import { ReportPdfType } from '../models/v2models/report-pdf-type';
import { ReviewerV2 } from '../models/v2models/reviewer-v2';
import { EditSectionTextResponse } from '../models/v2models/review-state';

export interface ReportV2Owner {
  userId: string;
  displayName: string;
  email: string;
}

export interface ReportV2 {
  reportId: number;
  reportName: string;
  reportType: string;
  clientName: string;
  projectId: string;
  owner: ReportV2Owner;
  reviewers: any[];
  dateCreated: string;
  lastUpdated: string;
  quality: number;
  siteName: string;
  status: number;
  statusLabel: string;
  docFileUrl: string | null;
}

export interface ReportV2SearchResponse {
  reports: ReportV2[];
}

export interface ReportV2Details {
  reportId: number;
  reportName: string;
  clientId: number;
  clientName: string;
  reportTypeId: number;
  reportTypeName: string;
  projectId: string;
  recipeId: number;
  status: number;
  statusLabel: string;
  owner: ReportV2Owner;
  reviewers: any[];
  currentJobId: string;
  userInput: Record<string, string | number | boolean | null>;
  qualityScore: number;
  dateCreated: string;
  docFileUrl: string | null;
  pdfFileUrl: string | null;
  // Review counters returned for reports in the update-from-data flow. Optional because
  // reports outside that flow do not carry them.
  dateFlags?: number;
  unchangedSections?: number;
  accepted?: number;
  declined?: number;
  pending?: number;
}

export interface RecipeSearchItem {
  recipeId: number;
  recipeName: string;
  description: string | null;
  version: number;
}

export interface VariableNumberConfig {
  minValue: number | null;
  maxValue: number | null;
  precision: number | null;
}

export interface VariableValidation {
  minLength: number | null;
  maxLength: number | null;
  regex: string | null;
  minItems: number | null;
  maxItems: number | null;
  minDate: string | null;
  maxDate: string | null;
  maxSpanDays: number | null;
  errorMessage: string | null;
}

export interface UserInputVariable {
  variableId: number;
  name: string;
  type: 'FreeText' | 'Number' | 'Selection' | 'MultiSelection' | 'Boolean' | 'Date';
  label: string;
  isRequired: boolean;
  defaultValue: string | null;
  options: string[] | null;
  numberConfig: VariableNumberConfig | null;
  validation: VariableValidation | null;
  sortOrder: number;
}

export interface ApprovalConfig {
  mode: string;
  minReviewers: number;
}

export interface RecipeDefinition {
  recipeId: number;
  recipeName: string;
  description: string;
  version: string;
  clientId: number;
  reportTypeId: number;
  userInputVariables: UserInputVariable[];
  approvalConfig: ApprovalConfig;
}

export interface CreateReportV2Request {
  reportName: string;
  clientId: number;
  reportTypeId: number;
  projectId: number;
  recipeId?: number;
  userInput: Record<string, any>;
  generateReport: boolean;
  uploadId?: string | null;
}

export interface ClientV2 {
  clientId: number;
  clientName: string;
  isActive: boolean;
}

export interface ReportTypeV2 {
  id: number;
  reportTypeName: string;
  isActive: boolean;
}

export interface ChartCountV2 {
  label: string;
  count: number;
}

export interface ReportsLoadEvent {
  append: boolean;
  success: boolean;
}

export interface UpdateReportStatusRequest {
  status: number;
}

@Injectable({
  providedIn: 'root',
})
export class ReportV2Service {
  private http = inject(HttpClient);
  private runtimeConfigService = inject(RuntimeConfigService);
  private snackbar = inject(SnackbarService);

  private baseApiUrl = this.runtimeConfigService.apiUrl;
  private apiVersion = this.runtimeConfigService.apiVersion;

  private reportsSignal = signal<ReportV2[]>([]);
  private clientsSignal = signal<ClientV2[]>([]);
  private reportTypesSignal = signal<ReportTypeV2[]>([]);
  private refreshReportsSource = new Subject<void>();
  private reportsLoadSource = new Subject<ReportsLoadEvent>();

  refreshChartData$ = this.refreshReportsSource.asObservable();
  reportsLoadCompleted$ = this.reportsLoadSource.asObservable();

  getReports(): Signal<ReportV2[]> {
    return computed(() => this.reportsSignal());
  }

  getClients(): Signal<ClientV2[]> {
    return computed(() => this.clientsSignal());
  }

  getReportTypes(): Signal<ReportTypeV2[]> {
    return computed(() => this.reportTypesSignal());
  }

  patchReportInList(reportId: number, patch: Partial<ReportV2>): void {
    this.reportsSignal.update((reports) =>
      reports.map((report) =>
        report.reportId === reportId ? { ...report, ...patch } : report
      )
    );
  }

  private getApiUrl(apiEndpoint: string): string {
    return buildEndpoint(this.baseApiUrl, this.apiVersion, 'reportservice', apiEndpoint);
  }

  getReportCount(): Observable<ApiResponse<ChartCountV2[]>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetCharts);
    return this.http.get<ApiResponse<ChartCountV2[]>>(apiUrl);
  }

  loadClients(): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetClients);
    this.http
      .get<ApiResponse<ClientV2[]>>(apiUrl)
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (clients) => this.clientsSignal.set(clients),
        error: () => {
          this.snackbar.showCustom({
            type: 'error',
            title: 'Error',
            message: 'Failed to load clients.',
          });
        },
      });
  }

  loadReportTypes(clientId: number): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetReportTypes);
    const params = new HttpParams().set('client_id', clientId.toString());
    this.http
      .get<ApiResponse<ReportTypeV2[]>>(apiUrl, { params })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (types) => this.reportTypesSignal.set(types),
        error: () => {
          this.snackbar.showCustom({
            type: 'error',
            title: 'Error',
            message: 'Failed to load report types.',
          });
        },
      });
  }

  clearReportTypes(): void {
    this.reportTypesSignal.set([]);
  }

  loadReports(pageNumber: number = 1, pageSize: number = 20, append: boolean = false): void {
    if (!append) {
      this.refreshReportsSource.next();
    }
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.RecentReports);
    this.http
      .post<ApiResponse<ReportV2SearchResponse>>(apiUrl, { pageNumber, pageSize })
      .pipe(
        tap((response: ApiResponse<ReportV2SearchResponse>) => {
          if (!response.success) {
            this.snackbar.showCustom({
              type: 'error',
              title: 'Error',
              message: response.message,
            });
          }
        }),
        map((response: ApiResponse<ReportV2SearchResponse>) => response.data?.reports ?? [])
      )
      .subscribe({
        next: (reports) => {
          if (append) {
            this.reportsSignal.update(existing => [...existing, ...reports]);
          } else {
            this.reportsSignal.set(reports);
          }
          this.reportsLoadSource.next({ append, success: true });
        },
        error: () => {
          this.reportsLoadSource.next({ append, success: false });
          this.snackbar.showCustom({
            type: 'error',
            title: 'Error',
            message: 'Failed to load reports.',
          });
        },
      });
  }

  searchRecipes(clientId: number, reportTypeId: number): Observable<ApiResponse<RecipeSearchItem[]>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.RECIPE.Search);
    let params = new HttpParams();
    params = params.set('clientId', clientId.toString());
    params = params.set('reportTypeId', reportTypeId.toString());
    return this.http.get<ApiResponse<RecipeSearchItem[]>>(apiUrl, { params });
  }

  getRecipeDefinition(recipeId: number): Observable<ApiResponse<RecipeDefinition>> {
    const apiUrl = this.getApiUrl(`${API_ENDPOINTS.REPORT_SERVICE.RECIPE.GetDefinition}/${recipeId}/definition`);
    return this.http.get<ApiResponse<RecipeDefinition>>(apiUrl);
  }

  createReport(request: CreateReportV2Request): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.CreateReport);
    return this.http.post<ApiResponse<any>>(apiUrl, request);
  }

  deleteReport(reportId: number): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(`${API_ENDPOINTS.REPORT_SERVICE.REPORTS.DeleteReport}/${reportId}`);
    return this.http.delete<ApiResponse<any>>(apiUrl);
  }

  updateReportStatus(reportId: number, request: UpdateReportStatusRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.UpdateStatus.replace('{reportId}', reportId.toString())
    );
    return this.http.patch<ApiResponse<boolean>>(apiUrl, request);
  }

  getSections(reportId: number): Observable<ApiResponse<ReportSectionsV2Response>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetSections.replace('{reportId}', reportId.toString())
    );
    return this.http.get<ApiResponse<ReportSectionsV2Response>>(apiUrl);
  }

  getReworkSections(reportId: number, sectionIds: number[]): Observable<ApiResponse<SectionV2[]>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetReworkSections.replace('{reportId}', reportId.toString())
    );
    return this.http.post<ApiResponse<SectionV2[]>>(apiUrl, { sectionIds });
  }

  getReviewers(clientId: number): Observable<ApiResponse<ReviewerV2[]>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REVIEWER.GetReviewers);
    return this.http.get<ApiResponse<ReviewerV2[]>>(apiUrl, { params: { clientId: clientId.toString() } });
  }

  getSectionEditText(reportId: number, sectionId: number): Observable<ApiResponse<EditSectionTextResponse>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.EditSectionText
        .replace('{reportId}', reportId.toString())
        .replace('{sectionId}', sectionId.toString())
    );
    return this.http.get<ApiResponse<EditSectionTextResponse>>(apiUrl);
  }

  updateSectionContent(reportId: number, sectionId: number, sectionContent: string, version?: string): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.UpdateSectionContent
        .replace('{reportId}', reportId.toString())
        .replace('{sectionId}', sectionId.toString())
    );
    const body: { sectionContent: string; version?: string } = { sectionContent };
    if (version) {
      body.version = version;
    }
    return this.http.put<ApiResponse<any>>(apiUrl, body);
  }

  triggerRework(reportId: number, sectionIds: number[], jobIdRef?: string): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.TriggerRework.replace('{reportId}', reportId.toString())
    );
    const body: { sectionIds: number[]; jobIdRef?: string } = { sectionIds };
    if (jobIdRef) {
      body.jobIdRef = jobIdRef;
    }
    return this.http.post<ApiResponse<any>>(apiUrl, body);
  }

  submitForReview(reportId: number, sectionId: number, reviewerIds: string[]): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.APPROVAL.SubmitForReview
        .replace('{reportId}', reportId.toString())
        .replace('{sectionId}', sectionId.toString())
    );
    return this.http.post<ApiResponse<any>>(apiUrl, { reviewerIds });
  }

  approveSection(reportId: number, sectionId: number, request: { reviewerId: string; feedback?: string | null; attemptNumber: number }): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.APPROVAL.ApproveSection
        .replace('{reportId}', reportId.toString())
        .replace('{sectionId}', sectionId.toString())
    );
    return this.http.post<ApiResponse<any>>(apiUrl, request);
  }

  rejectSection(reportId: number, sectionId: number, request: { reviewerId: string; feedback?: string | null; attemptNumber: number }): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.APPROVAL.RejectSection
        .replace('{reportId}', reportId.toString())
        .replace('{sectionId}', sectionId.toString())
    );
    return this.http.post<ApiResponse<any>>(apiUrl, request);
  }

  addComment(reportId: number, request: { sectionId: number; sectionNumber?: string; comment: string }): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.COMMENTS.AddComment.replace('{reportId}', reportId.toString())
    );
    return this.http.post<ApiResponse<any>>(apiUrl, request);
  }

  getComments(reportId: number, sectionId?: number): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.COMMENTS.GetComments.replace('{reportId}', reportId.toString())
    );
    let params = new HttpParams();
    if (sectionId) {
      params = params.set('sectionId', sectionId.toString());
    }
    return this.http.get<ApiResponse<any>>(apiUrl, { params });
  }

  deleteComment(reportId: number, sectionId: number, commentId: string): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.COMMENTS.DeleteComment
        .replace('{reportId}', reportId.toString())
        .replace('{sectionId}', sectionId.toString())
        .replace('{commentId}', commentId)
    );
    return this.http.delete<ApiResponse<any>>(apiUrl);
  }

  publishReport(reportId: number): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.PublishReport.replace('{reportId}', reportId.toString())
    );
    return this.http.post<ApiResponse<any>>(apiUrl, {});
  }

  getReportDetails(reportId: number): Observable<ApiResponse<ReportV2Details>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetReportDetails.replace('{reportId}', reportId.toString())
    );
    return this.http.get<ApiResponse<ReportV2Details>>(apiUrl);
  }

  getReportPdfStream(reportId: number, type: ReportPdfType): Observable<HttpResponse<Blob>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.FILE_PROVISIONING.ReportPdfStream
        .replace('{reportId}', reportId.toString())
        .replace('{type}', type.toString())
    );

    return this.http.get(apiUrl, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  retryReportGeneration(reportId: number): Observable<ApiResponse<any>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.RetryReportGeneration.replace('{reportId}', reportId.toString())
    );
    return this.http.post<ApiResponse<any>>(apiUrl, {});
  }
}
